/**
 * The WhatsApp session token — the thing that stitches a browser session to
 * the conversation it hands off to.
 *
 * ## The loop this closes
 *
 * A renter clicks a WhatsApp CTA. We embed `?sid={token}` (a 4-char hash of
 * the live PostHog session id) in the prefilled listing URL. They chat;
 * LangGraph qualifies them; the server fires `qualified_whatsapp_lead` with
 * `distinctId: session:{token}` (see `agent.ts`).
 *
 * Without an alias, that server event lands on a BRAND NEW PostHog person.
 * The same human is then two persons — the browser one that clicked, and the
 * `session:{token}` one that chatted — which double-counts them in every
 * lead denominator. That inflation is heaviest on the funnels with the most
 * WhatsApp share (Traditional + the `unknown` WA bucket), so it makes those
 * conversion rates read LOWER than they are. `aliasWaSession` is what
 * collapses the two back into one person.
 *
 * ## Two rules this module exists to make un-forgettable
 *
 * 1. **Alias only when the prefill actually carries the `sid`.** The alias
 *    is meaningless unless the server can read the token back out of the
 *    conversation. `SpecialistCard` also renders on supply pages with
 *    `location: null`, where no token rides the message — aliasing there
 *    would identify host-side clickers for zero attribution benefit.
 * 2. **Alias on CLICK, never on mount.** Under `person_profiles:
 *    'identified_only'`, `alias` creates a person profile and every
 *    subsequent event for that person is billed as identified. `WrapperWA`
 *    renders on every Monthly listing page, so aliasing at mount would
 *    identify every listing VIEWER — blowing up precisely the cost that
 *    config was chosen to control. Clicking a WA CTA is the intent signal
 *    that earns the profile, and it's the same population the server fires
 *    `qualified_whatsapp_lead` for.
 *
 * Render-time callers (SSR'd `<a href>`) must read the token through
 * `useWaSessionToken` instead of calling `getWaSessionToken` directly — see
 * that hook for why.
 */

import posthog from 'src/utils/pixels/posthog'
import { firedOncePerSession } from 'src/utils/pixels/sessionDedup'
import { hashSessionId } from 'src/utils/sessionToken'

/**
 * Live-read the current session's WhatsApp token. Returns `''` before
 * PostHog initializes (and on SSR / internal traffic), in which case callers
 * omit the token line and the backend falls back to phone-based attribution
 * via `findWhatsappAttribution`.
 *
 * Pure read — no alias. Use `claimWaSessionToken` when the returned token is
 * going straight into a prefill.
 */
export function getWaSessionToken(): string {
  return hashSessionId(posthog.sessionId())
}

/**
 * Alias `session:{token}` onto the current browser person so the server's
 * later `qualified_whatsapp_lead` merges into it instead of spawning a
 * second person. Fires at most once per session; no-ops on an empty token.
 *
 * Call this from WhatsApp CLICK handlers only, passing the token that was
 * actually embedded in the outbound prefill (rule 1 above).
 *
 * Token collisions (4 chars = ~1M buckets) would make two browsers claim the
 * same alias; PostHog refuses the second merge rather than fusing unrelated
 * people, so a collision degrades to today's unstitched behavior. At WA-click
 * volume that's well under 1% even cumulatively — not worth a wider token.
 */
export function aliasWaSession(token: string): void {
  if (!token) return
  // Dedupe INSIDE the fire so a session that never initializes PostHog can't
  // burn its one shot. (A non-empty token already implies PostHog is live, so
  // this is belt-and-braces rather than a live failure mode.)
  if (firedOncePerSession(`wa_alias:${token}`)) return
  posthog.alias(`session:${token}`)
}

/**
 * Read the token AND alias the session in one step. For click handlers that
 * feed the return value straight into `buildLeadFormWaMessage` — which makes
 * "the token we aliased" and "the token we embedded" the same value by
 * construction, so rule 1 can't drift.
 */
export function claimWaSessionToken(): string {
  const token = getWaSessionToken()
  aliasWaSession(token)
  return token
}
